import React, { useEffect, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";

/** Compact copy icon. Hooks can't live in a column formatter, hence the nested component. */
const CopyRefButton = ({ text, title }) => {
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => setCopied(false), 1500);
        return () => clearTimeout(timer);
    }, [copied]);
    return (
        <CopyToClipboard text={text} onCopy={() => setCopied(true)}>
            <i
                className={copied
                    ? "bi bi-clipboard-check-fill text-success"
                    : "bi bi-clipboard text-muted"}
                style={{ cursor: "pointer", fontSize: "11px", opacity: copied ? 1 : 0.5, lineHeight: 1 }}
                title={copied ? "Copied!" : title}
            ></i>
        </CopyToClipboard>
    );
};

const TONES = {
    success: { fg: "#0f9d58", bg: "#e8f5ec" },
    primary: { fg: "#3f4ee0", bg: "#eceefc" },
    danger: { fg: "#d9534f", bg: "#fbeaea" },
    muted: { fg: "#7e8299", bg: "#f1f1f4" },
};

/**
 * Work out what to display for one requested document.
 *
 * Unlike the server, the outpost also has to represent work that has not happened yet: a sale
 * is recorded offline and the document is only created once it syncs. So "Pending" and
 * "Failed" are first-class states here, and Failed stays clickable because the detail dialog
 * is where the operator fixes the data and retries.
 */
function describe({ requested, status, reference, label, tone, shortener }) {
    if (!requested) return null;
    const state = String(status || "").toUpperCase();

    if (state === "GENERATED") {
        return {
            label, tone, clickable: true,
            text: shortener ? shortener(reference) : String(reference || "Created"),
            copy: reference ? String(reference) : null,
        };
    }
    if (state === "FAILED") {
        return { label, tone: "danger", clickable: true, text: "Failed", failed: true };
    }
    if (state === "CANCELLED") {
        return {
            label, tone: "danger", clickable: true, strike: true,
            text: reference ? (shortener ? shortener(reference) : String(reference)) : "Cancelled",
        };
    }
    return { label, tone: "muted", pending: true, text: "Pending" };
}

/**
 * The GST documents for one sale in a single table cell.
 *
 * Replaces separate "E-Way Bill" and "E-Invoice" columns — one question, one place to look.
 * Laid out as a three-column grid (label, reference, copy) so the two lines align with each
 * other and with neighbouring rows.
 */
export function GstDocumentsCell({ row, einvoiceEnabled, onOpenEway, onOpenEinvoice }) {
    const lines = [];

    const einv = einvoiceEnabled && describe({
        requested: row.generateEinvoice,
        status: row.einvoiceStatus,
        reference: row.irn,
        label: "IRN",
        tone: "success",
        // The IRN is 64 characters; show a recognisable head and copy the whole thing.
        shortener: (v) => (v ? `${String(v).slice(0, 10)}…` : "Registered"),
    });
    if (einv) lines.push({ ...einv, key: "irn", onOpen: onOpenEinvoice, copyTitle: "Copy IRN" });

    const eway = describe({
        requested: row.generateEwayBill,
        status: row.ewayBillStatus,
        reference: row.ewayBillNo,
        label: "EWB",
        tone: "primary",
    });
    if (eway) lines.push({ ...eway, key: "ewb", onOpen: onOpenEway, copyTitle: "Copy e-Way bill number" });

    if (lines.length === 0) {
        return <span className="text-muted">-</span>;
    }

    const error = lines.some((l) => l.failed)
        ? (row.einvoiceError || row.ewayBillError || "")
        : null;

    return (
        <span
            style={{
                display: "inline-grid",
                gridTemplateColumns: "34px minmax(92px, auto) 14px",
                columnGap: "8px",
                rowGap: "5px",
                alignItems: "center",
                textAlign: "left",
            }}
        >
            {lines.map((l) => {
                const tone = TONES[l.tone] || TONES.muted;
                return (
                    <React.Fragment key={l.key}>
                        <span
                            style={{
                                color: tone.fg,
                                backgroundColor: tone.bg,
                                fontSize: "9px",
                                fontWeight: 700,
                                letterSpacing: "0.4px",
                                textAlign: "center",
                                borderRadius: "4px",
                                padding: "2px 0",
                                lineHeight: 1.4,
                            }}
                        >
                            {l.label}
                        </span>

                        <span
                            onClick={l.clickable || l.pending ? l.onOpen : undefined}
                            title={l.failed
                                ? "Click to see why and regenerate"
                                : l.pending
                                    ? "Will be created when this sale syncs to the server"
                                    : "Click for details"}
                            className={l.strike || l.pending ? "text-muted" : l.failed ? "text-danger" : "text-primary"}
                            style={{
                                cursor: l.clickable || l.pending ? "pointer" : "default",
                                fontFamily: l.pending || l.failed ? "inherit" : "Menlo, Consolas, monospace",
                                fontVariantNumeric: "tabular-nums",
                                fontSize: l.pending || l.failed ? "11px" : "11.5px",
                                fontWeight: 600,
                                letterSpacing: "-0.2px",
                                textDecoration: l.strike ? "line-through" : "none",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {l.pending && <i className="bi bi-hourglass-split me-1"></i>}
                            {l.failed && <i className="bi bi-exclamation-circle me-1"></i>}
                            {l.text}
                        </span>

                        {l.copy ? <CopyRefButton text={l.copy} title={l.copyTitle} /> : <span />}
                    </React.Fragment>
                );
            })}

            {error && (
                <span
                    className="text-danger"
                    style={{ gridColumn: "1 / -1", fontSize: "10px", lineHeight: 1.3 }}
                    title={error}
                >
                    {error.length > 60 ? `${error.slice(0, 60)}…` : error}
                </span>
            )}
        </span>
    );
}
